import React from "react";
import { isEmpty } from "lodash";
import moment from "moment";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Table from "examples/Tables/Table";
import Author from "layouts/dashboard/components/TableAuthor";
import GoalActionButton from "layouts/goals/components/GoalActionButton";
import GoalPill from "layouts/goals/components/GoalPill";
import {
  EMPLOYEES_COLUMNS,
  EMPLOYEES_MESSAGES,
  EMPLOYMENT_STATUSES,
  EMPLOYEE_TYPES,
  WORK_MODES,
  labelFor,
} from "../employees.constants";

const muted = (text) => (
  <SuiTypography variant="caption" color="secondary">
    {text || "—"}
  </SuiTypography>
);

// `assetGroups` and `onAssets` are only passed to people who may manage assets.
// Company assets they hold, plus a warning when someone leaving still has some.
const assetsCell = (employee, assetGroups) => {
  const held = assetGroups?.[employee.user_slack_id]?.held.length || 0;
  if (held === 0) return muted("—");
  const leaving = employee.employment_status !== "active";
  return <GoalPill color={leaving ? "warning" : "info"}>{held}</GoalPill>;
};

function EmployeesTable({ employees, onEdit, onArchive, isLoading, assetGroups, onAssets }) {
  const showAssets = !!onAssets;
  const columns = showAssets
    ? [
        ...EMPLOYEES_COLUMNS.slice(0, -1),
        { name: "assets", align: "center" },
        ...EMPLOYEES_COLUMNS.slice(-1),
      ]
    : EMPLOYEES_COLUMNS;
  const rows = employees.map((employee) => {
    const status = EMPLOYMENT_STATUSES.find((s) => s.value === employee.employment_status);
    const workMode = WORK_MODES.find((mode) => mode.value === employee.work_mode);
    return {
      employee: (
        <Author
          name={employee.name || employee.user_name}
          email={employee.user_name}
          image={employee.image_url}
          jobTitle={employee.job_title}
        />
      ),
      "employee id": muted(employee.employee_id),
      type: muted(employee.employee_type && labelFor(EMPLOYEE_TYPES, employee.employee_type)),
      "work mode": (
        <GoalPill color={workMode?.color}>{workMode?.label || employee.work_mode}</GoalPill>
      ),
      joined: muted(employee.joining_date && moment(employee.joining_date).format("DD MMM YYYY")),
      status: (
        <GoalPill color={status?.color}>{status?.label || employee.employment_status}</GoalPill>
      ),
      assets: showAssets ? assetsCell(employee, assetGroups) : null,
      actions: (
        <SuiBox display="flex" gap={1} alignItems="center" justifyContent="center">
          <GoalActionButton
            icon="edit"
            tooltip="Edit employee"
            color="info"
            onClick={() => onEdit(employee)}
          />
          {showAssets && (
            <GoalActionButton
              icon="devices"
              tooltip="Assets"
              color="success"
              onClick={() => onAssets(employee)}
            />
          )}
          <GoalActionButton
            icon="person_remove"
            tooltip={employee.is_tracked ? "Archive employee" : "Already archived"}
            color="error"
            disabled={!employee.is_tracked}
            onClick={() => onArchive(employee)}
          />
        </SuiBox>
      ),
    };
  });

  return (
    <Card>
      <SuiBox p={3}>
        {isLoading ? (
          <SuiBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress color="dark" size={30} />
          </SuiBox>
        ) : !isEmpty(rows) ? (
          <Table columns={columns} rows={rows} />
        ) : (
          <SuiBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
              badge
            </Icon>
            <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
              {EMPLOYEES_MESSAGES.empty}
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );
}

export default EmployeesTable;
